import { useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';

type KeyCombo = {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
};

export function useKeyboardShortcut(
  combo: KeyCombo,
  callback: () => void,
  enabled = true
) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const { key, ctrl, shift, alt, meta } = combo;

      // Verificar se event.key existe antes de usar toLowerCase
      if (!event.key) return;

      const ctrlMatch = ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
      const shiftMatch = shift ? event.shiftKey : !event.shiftKey;
      const altMatch = alt ? event.altKey : !event.altKey;
      const metaMatch = meta ? event.metaKey : true;

      const isMatch =
        event.key.toLowerCase() === key.toLowerCase() &&
        ctrlMatch &&
        shiftMatch &&
        altMatch &&
        metaMatch;

      if (isMatch) {
        // Prevenir ação padrão do navegador IMEDIATAMENTE
        event.preventDefault();
        event.stopPropagation();
        callback();
      }
    },
    [combo, callback]
  );

  useEffect(() => {
    if (!enabled) return;

    // Usar capture: true para capturar o evento antes que chegue aos elementos
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [enabled, handleKeyDown]);
}

export function useGlobalShortcuts() {
  const { lock } = useAuthStore.getState();

  // Ctrl+Shift+L to lock (evita conflito com atalho do navegador)
  useKeyboardShortcut({ key: 'l', ctrl: true, shift: true }, () => {
    lock();
  });
}
